import React, { useCallback, useEffect, useState } from 'react';
import PageHeader from '@/components/PageHeader';
import AdminBillingNav from '@/components/adminBilling/AdminBillingNav';
import StatusChip from '@/components/adminBilling/StatusChip';
import ConfirmActionModal from '@/components/ConfirmActionModal';
import { adminBillingService } from '@/api/services/adminBilling';
import { formatApiError } from '@/utils/formatApiError';
import { useMe } from '@/context/MeContext';
import { Navigate } from 'react-router-dom';
import { DataTableScrollArea } from '@/components/table';

export default function AdminBillingWebhooksPage() {
  const { me } = useMe();
  const canReplay = me?.permissions.canRunManualBillingOps;
  const [items, setItems] = useState<Record<string, unknown>[]>([]);
  const [selected, setSelected] = useState<Record<string, unknown> | null>(null);
  const [provider, setProvider] = useState('');
  const [error, setError] = useState<string | null>(null);
  const [replayId, setReplayId] = useState<string | null>(null);

  const load = useCallback(() => {
    adminBillingService
      .webhooks({ provider: provider || undefined })
      .then((res) => setItems(res.items))
      .catch((e) => setError(formatApiError(e)));
  }, [provider]);

  useEffect(() => {
    load();
  }, [load]);

  const viewPayload = async (id: string) => {
    try {
      const detail = await adminBillingService.webhookDetail(id);
      setSelected(detail);
    } catch (e) {
      setError(formatApiError(e));
    }
  };

  const replay = async (id: string) => {
    setReplayId(id);
  };

  const executeReplay = async () => {
    if (!replayId) return;
    try {
      await adminBillingService.replayWebhook(replayId);
      load();
    } catch (e) {
      setError(formatApiError(e));
    } finally {
      setReplayId(null);
    }
  };

  const isPlatform =
    me?.role === 'owner' || me?.role === 'platform_admin' || me?.role === 'admin';
  if (!isPlatform) return <Navigate to="/admin/billing" replace />;

  return (
    <div className="admin-billing-page">
      <AdminBillingNav />
      <PageHeader title="Webhook events" subtitle="Read-only explorer with optional replay (owner)." />
      {error ? <div className="alert alert-error">{error}</div> : null}
      <section className="panel">
        <select value={provider} onChange={(e) => setProvider(e.target.value)}>
          <option value="">All providers</option>
          <option value="stripe">Stripe</option>
          <option value="paypal">PayPal</option>
          <option value="manual">Manual</option>
        </select>
        <DataTableScrollArea>
        <table className="data-table">
          <thead>
            <tr>
              <th>Received</th>
              <th>Provider</th>
              <th>Event</th>
              <th>Status</th>
              <th>Signature</th>
              <th>Size</th>
              <th />
            </tr>
          </thead>
          <tbody>
            {items.map((w) => (
              <tr key={String(w.id)}>
                <td>{w.receivedAt ? new Date(String(w.receivedAt)).toLocaleString() : '—'}</td>
                <td>{String(w.provider)}</td>
                <td>{String(w.eventType)}</td>
                <td><StatusChip status={String(w.eventStatus)} /></td>
                <td>{w.signatureValid === true ? '✓' : w.signatureValid === false ? '✗' : '—'}</td>
                <td>{String(w.payloadSizeBytes || 0)} B</td>
                <td>
                  <button type="button" className="btn btn-secondary btn-sm" onClick={() => viewPayload(String(w.id))}>
                    View
                  </button>
                  {canReplay ? (
                    <button type="button" className="btn btn-secondary btn-sm" onClick={() => replay(String(w.id))}>
                      Replay
                    </button>
                  ) : null}
                </td>
              </tr>
            ))}
          </tbody>
        </table>
        </DataTableScrollArea>
      </section>
      {selected ? (
        <section className="panel">
          <h2>Payload</h2>
          <pre className="admin-billing-payload">{JSON.stringify(selected.payload, null, 2)}</pre>
          <button type="button" className="btn btn-secondary" onClick={() => setSelected(null)}>Close</button>
        </section>
      ) : null}
      <ConfirmActionModal
        open={Boolean(replayId)}
        title="Replay webhook event?"
        description="This will re-process the webhook payload through the billing pipeline."
        confirmLabel="Replay"
        onCancel={() => setReplayId(null)}
        onConfirm={executeReplay}
      />
    </div>
  );
}
